import { CircularProgress } from '@mui/material'
import { ClaimFreeMonthDialog } from '../ClaimFreeMonthDialog'
import { FullPageErrorFallback } from '@dtx-company/inter-app/src/components/PageErrorFallback'
import { LoggerErrorBoundary } from '@dtx-company/logger'
import { PaymentMethod } from '@stripe/stripe-js'
import { PaymentStep } from '@app/common/src/components/PlanPurchaseExperience/PaymentStep'
import { PlanCostSummary } from '@dtx-company/inter-app/src/types/PlanCostSummary'
import {
  PlanPurchaseExperienceContext,
  usePlanPurchaseExperienceContext
} from './PlanPurchaseExperienceContext'
import {
  PlanPurchaseExperienceMainContentMinWidth,
  PlanPurchaseExperiencePromoContentFullWidth
} from './constants'
import { PlanPurchaseExperienceMode } from '@dtx-company/true-common/src/types/Plan'
import { PlanPurchaseExperienceProps } from './types'
import { PromoCodeSection } from '@app/common/src/components/PlanPurchaseExperience/components/PromoCodeSection'
import { StripeElements } from '@app/common/src/components/PlanPurchaseExperience/components/StripeElements'
import { SubscriptionErrorMessage } from '@app/common/src/components/PlanPurchaseExperience/components/SubscriptionErrorMessage'
import {
  _MainContentBox,
  _RootBoxPlanPurchaseExperience
} from '@app/common/src/components/PlanPurchaseExperience/components/PlanPurchaseExperience.styles'
import { fireAnalyticsEvent } from '@dtx-company/inter-app/src/event-tracking/helpers/fireAnalyticsEvent'
import { getConversionSource } from '@app/common/src/components/PlanPurchaseExperience/utils/getConversionSource'
import { getConversionType } from '@app/common/src/components/PlanPurchaseExperience/utils/getConversionType'
import { getCoupon } from '@dtx-company/inter-app/src/api/subscription-service/getCoupon'
import { getPlanTypeDirection } from '../../utils/getPlanTypeDirection'
import { isPilotPlanType } from '../../utils/isPilotPlanType'
import { useAPISWR } from '@dtx-company/inter-app/src/hooks/useAPISWR/useAPISWR'
import { useEffect, useRef, useState } from 'react'
import { useFlowRouter } from '@dtx-company/inter-app/src/hooks/useFlowRouter'
import { useHubSpotUserIthacaRoleSync } from '../../../../../components/_app/hooks/useHubSpotUserSync'
import { useOnce } from '@dtx-company/true-common/src/hooks/useOnce'
import { useScroll } from 'framer-motion'
import events from '@dtx-company/inter-app/src/event-tracking/events/proConversion'

function InnerPlanPurchaseExperience(props: PlanPurchaseExperienceProps): JSX.Element {
  const flowRouter = useFlowRouter()
  const planType = props.planType
  const context = usePlanPurchaseExperienceContext({ ...props, planType: planType })
  const { authState, plans: availablePlans } = context
  const { selectedPlan, setSelectedPlan } = context
  const { currentSubscriptionInfoSWR, currentSubscriptionInfoData } = context
  const mode = props.mode

  const [existingPaymentMethod, setExistingPaymentMethod] = useState<PaymentMethod | undefined>(
    undefined
  )

  const [isFreeMonthDialogOpen, setIsFreeMonthDialogOpen] = useState(false)
  const mainContentBoxRef = useRef<HTMLElement | undefined>(undefined)

  const currentPlan = currentSubscriptionInfoData?.plan
  const currentPlanExtraDetails =
    selectedPlan?.type &&
    currentSubscriptionInfoData?.plan.type === selectedPlan?.type &&
    currentSubscriptionInfoData.planExtraDetails.length
      ? currentSubscriptionInfoData.planExtraDetails
      : undefined
  const [planCostSummary, setPlanCostSummary] = useState<PlanCostSummary | undefined>(undefined)

  const canViewSavedCard = authState.hasOperation('view:saved_card_payment')
  // Determine if should show existing card by default
  useEffect(() => {
    if (!currentSubscriptionInfoData) return
    if (!authState.isAuthChecked) return
    setExistingPaymentMethod(
      canViewSavedCard ? currentSubscriptionInfoData?.paymentMethod : undefined
    )
  }, [
    currentSubscriptionInfoData,
    canViewSavedCard,
    authState.isAuthChecked,
    currentSubscriptionInfoData?.paymentMethod
  ])
  // HubSpot user sync. Update hubspot users with new Ithaca role (new plan)
  useHubSpotUserIthacaRoleSync()

  const viewportScroll = useScroll()

  // TODO: this should be deleted at some point when we hoist the fetch-on-mount out of promo input - LD
  const promoCode = flowRouter.getString('promoCode')
  const promoFromQuerySWR = useAPISWR(
    getCoupon,
    () => {
      if (!promoCode) return
      return { code: promoCode }
    },
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnMount: true
    }
  )
  const promoFromQuery = promoFromQuerySWR.data
  const promoFromQueryHasLoaded = !flowRouter.query.promoCode || promoFromQuery !== undefined
  // Select initial plan
  useOnce(() => {
    if (
      !promoFromQueryHasLoaded ||
      !availablePlans?.length ||
      !authState.isAuthChecked ||
      currentSubscriptionInfoSWR.isValidating ||
      (authState.isAuthenticated && currentSubscriptionInfoData === undefined)
    ) {
      return false
    }

    if (
      mode === PlanPurchaseExperienceMode.CHANGE_PAYMENT ||
      mode === PlanPurchaseExperienceMode.PURCHASE_ADDONS
    ) {
      setSelectedPlan(currentPlan)
      return
    }

    const plansThatAreNotCurrentPlan = availablePlans.filter(plan => {
      return plan.type !== currentPlan?.type || plan.interval !== currentPlan?.interval
    })

    const plansValidForQueryPromo = plansThatAreNotCurrentPlan.filter(plan => {
      if (!promoFromQuery) return true
      if (promoFromQuery.planIds.length === 0) return true
      return promoFromQuery.planIds.includes(plan.id)
    })

    if (plansValidForQueryPromo.length) {
      setSelectedPlan(plansValidForQueryPromo[0])
    } else if (plansThatAreNotCurrentPlan.length) {
      setSelectedPlan(plansThatAreNotCurrentPlan[0])
    } else {
      setSelectedPlan(availablePlans[0])
    }
  })

  useOnce(() => {
    if (mode === PlanPurchaseExperienceMode.CHANGE_PAYMENT) return // never run it
    if (!selectedPlan || currentSubscriptionInfoSWR.isValidating || !authState.isAuthChecked) {
      return false
    }

    events.userStartedProConversion({
      email: authState.jwt?.email ?? '',
      lockSelected: context.heapLockSelected,
      planType: planType,
      planSelected: selectedPlan.id,
      currentUserPlan: currentPlan?.id,
      codeLimit: authState.codeLimit,
      pageLimit: authState.pageLimit,
      conversionSource: getConversionSource(flowRouter),
      isLoggedIn: authState.isAuthenticated,
      conversionType: getConversionType(currentPlan, selectedPlan, mode)
    })
  })

  // Determine if should show claim free month modal
  useEffect(() => {
    if (
      !props.onClaimFreeMonth ||
      !currentSubscriptionInfoData?.eligibleForFreeMonth ||
      !currentPlan?.type ||
      !selectedPlan?.type
    ) {
      return
    }
    const isPilot = isPilotPlanType(currentPlan.type)
    if (!isPilot && getPlanTypeDirection(currentPlan.type, selectedPlan.type) < 0) {
      setIsFreeMonthDialogOpen(true)
    }
  }, [
    props.onClaimFreeMonth,
    currentSubscriptionInfoData?.eligibleForFreeMonth,
    currentPlan?.type,
    selectedPlan?.type
  ])

  const isPaymentDataLoading =
    !availablePlans ||
    !selectedPlan ||
    currentSubscriptionInfoData === undefined ||
    !promoFromQueryHasLoaded

  const subscriptionError = currentSubscriptionInfoSWR.error?.message || null

  return (
    <PlanPurchaseExperienceContext.Provider value={context}>
      <StripeElements amount={planCostSummary?.grandTotalInCents}>
        <_RootBoxPlanPurchaseExperience
          widthClasses={{
            wide:
              PlanPurchaseExperienceMainContentMinWidth +
              PlanPurchaseExperiencePromoContentFullWidth
          }}
        >
          <_MainContentBox ref={mainContentBoxRef}>
            {subscriptionError ? (
              <SubscriptionErrorMessage />
            ) : (
              <>
                {isPaymentDataLoading ? (
                  <CircularProgress
                    color="primary"
                    sx={{ m: '0 auto' }}
                    data-testid={'loading-in-progress'}
                  ></CircularProgress>
                ) : (
                  <PaymentStep
                    selectedPlan={selectedPlan}
                    currentPlan={currentPlan}
                    currentPlanExtraDetails={currentPlanExtraDetails}
                    onSelectedPlanChange={value => {
                      setSelectedPlan(value)
                      fireAnalyticsEvent('Revenue_Upgrade_Selected_Plan', {
                        planSelected: value.id
                      })
                    }}
                    activeSubscriptionId={currentSubscriptionInfoData?.id}
                    existingPaymentMethod={existingPaymentMethod}
                    stickyBottom={props.stickyBottom}
                    isTotalFloating={
                      // should technically be < 1 but it wasn't always reaching 1
                      // for some reason so using 0.99
                      viewportScroll.scrollYProgress.get() < 0.99
                    }
                    onSuccessfulCheckout={props.onSuccessfulCheckout}
                    showLearnMore={props.showLearnMore}
                    showChangeAddons={Boolean(currentPlanExtraDetails)}
                    mode={mode}
                    planCostSummary={planCostSummary}
                    setPlanCostSummary={setPlanCostSummary}
                  />
                )}
              </>
            )}
          </_MainContentBox>
          <PromoCodeSection
            promoBackgroundImageUrl={props.promoBackgroundImageUrl}
            selectedPlan={selectedPlan}
            promoHeading={props.promoHeading}
            promoAsset={props.promoAsset}
            promoHideCompanies={props.promoHideCompanies}
            mode={mode}
          />
        </_RootBoxPlanPurchaseExperience>
      </StripeElements>
      {props.onClaimFreeMonth && (
        <ClaimFreeMonthDialog
          open={isFreeMonthDialogOpen}
          onAcceptSuccess={async () => {
            setIsFreeMonthDialogOpen(false)
            if (props.onClaimFreeMonth) await props.onClaimFreeMonth()
          }}
          onDecline={() => {
            setIsFreeMonthDialogOpen(false)
          }}
          onClose={() => {
            setIsFreeMonthDialogOpen(false)
          }}
        />
      )}
    </PlanPurchaseExperienceContext.Provider>
  )
}

export function PlanPurchaseExperience(props: PlanPurchaseExperienceProps): JSX.Element {
  return (
    <LoggerErrorBoundary options={{ team: 'monetization' }} fallback={<FullPageErrorFallback />}>
      <InnerPlanPurchaseExperience {...props} />
    </LoggerErrorBoundary>
  )
}
